import moment from 'moment';
import { Button } from '@acpaas-ui/react-components';
import _ from 'lodash';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import DossierCasetafelAttachments from '../../../components/dossier-casetafel/DossierCasetafelAttachments';
import DossierCasetafelDetails from '../../../components/dossier-casetafel/DossierCasetafelDetails';
import DossierCasetafelStakeholders from '../../../components/dossier-casetafel/DossierCasetafelStakeholders';
import DossierCasetafelQuestionnaire from '../../../components/dossier-casetafel/questionnaire/DossierCasetafelQuestionnaire';
import DossierSectionWrapper from '../../../components/dossier-details/DossierSectionWrapper';
import DossierSubitemActionBar from '../../../components/dossier-shared/DossierSubitemActionBar';
import { STAKEHOLDER_DOSSIER_PATHS } from '../../../constants/dossier.constants';
import { MODALS } from '../../../constants/modals.constants';
import { ATTACHMENT_TYPES, BFF_HOST, DOSSIER_TYPES, TOAST_TYPES } from '../../../constants/settings.constants';
import { useAttachmentCRUD } from '../../../hooks/useAttachmentCRUD.hook';
import { useModal } from '../../../hooks/useModal.hook';
import {
  commissionToTitle,
  getAttachmentsByTypes,
  getAttachmentsForCasetafel,
  getAttachmentsForCasetafelReport
} from '../../../lib/dossier.utils';
import AttachmentDetailsModal from '../../../modals/AttachmentDetailsModal';
import AttachmentUploadModal from '../../../modals/AttachmentUploadModal';
import CasetafelApproveModal from '../../../modals/CasetafelApproveModal';
import CasetafelRejectModal from '../../../modals/CasetafelRejectModal';
import DeleteWarningModal from '../../../modals/DeleteWarningModal';
import StakeholderAddModal from '../../../modals/StakeholderAddModal';
import StakeholderDetailModal from '../../../modals/StakeholderDetailModal';
import casetafelsService from '../../../services/api/casetafels.service';
import dossierTabsPermissionsService from '../../../services/permissions/dossier.tabs.permissions.service';
import permissionsService from '../../../services/permissions/permissions.service';
import { getToken } from '../../../services/session.storage';
import toastService from '../../../services/toasts.service';
import { attachments as attachmentsBff } from '../../../services/bff.api';

const DossierDetailCommission = ({
  data,
  isLoadingAttachments,
  onDossierRefetch,
  onAttachmentsRefetch,
  onUpdated,
  attachments,
  forms,
  isLoadingForms,
  user,
  openedCasetables,
  setOpenedCasetables,
  permissions
}) => {
  const params = useParams();

  const deleteModal = useModal(MODALS.CASETAFEL_CONFIRM_DELETE);
  const approveModal = useModal(MODALS.CASETAFEL_APPROVE);
  const rejectModal = useModal(MODALS.CASETAFEL_REJECT);
  const attachmentCRUD = useAttachmentCRUD(params.id);
  const stakeholderAddModal = useModal(MODALS.STAKEHOLDER_ADD_MODAL);
  const stakeholderDetailModal = useModal(MODALS.STAKEHOLDER_DETAIL_MODAL);

  const [stakeholderCasetafelId, setStakeholderCasetafelId] = useState('');
  const [stakeholderDetailModalData, setStakeholderDetailModalData] = useState({});
  const [modalPermissions, setModalPermissions] = useState({
    write: false,
    read: false
  });
  const [deletingCasetafelId, setDeletingCasetafelId] = useState('');
  const [approveRejectCasetafelId, setApproveRejectCasetafelId] = useState('');
  const [creatingCasetafel, setCreatingCasetafel] = useState(false);
  const [openedCasetafel, setOpenedCasetafel] = useState(null);

  const handleAdd = () => {
    setCreatingCasetafel(true);
    const latestClarificationAttachment = _.head(
      _.sortBy(getAttachmentsByTypes(attachments, [ATTACHMENT_TYPES.CLARIFICATION]), (a) => a.createdAt).reverse()
    );
    casetafelsService.add(
      params.id,
      { requestDescription: latestClarificationAttachment?.description ?? '' },
      {
        onSuccess: (resp) => {
          setCreatingCasetafel(false);
          onDossierRefetch();
          setOpenedCasetables([resp.id]);
        },
        onError: (message) => {
          toastService.showToast(TOAST_TYPES.ERROR, message);
          setCreatingCasetafel(false);
        }
      }
    );
  };

  const handleRequestDelete = (id) => {
    setDeletingCasetafelId(id);
    deleteModal.onOpen();
  };

  const handleDelete = (onError, onSuccess) => {
    casetafelsService.remove(params.id, deletingCasetafelId, {
      onSuccess: (resp) => {
        onSuccess(resp);
        onDossierRefetch();
      },
      onError: (err) => onError(err.message)
    });
  };

  const handleRequestApprove = (id) => {
    setApproveRejectCasetafelId(id);
    approveModal.onOpen();
  };

  const handleRequestReject = (id) => {
    setApproveRejectCasetafelId(id);
    rejectModal.onOpen();
  };

  const handleApprove = (remark, onError, onSuccess) => {
    casetafelsService.approve(
      params.id,
      approveRejectCasetafelId,
      { message: remark },
      {
        onSuccess: () => {
          onSuccess();
          onDossierRefetch();
        },
        onError
      }
    );
  };

  const handleReject = (remark, onError, onSuccess) => {
    casetafelsService.reject(
      params.id,
      approveRejectCasetafelId,
      { message: remark },
      {
        onSuccess: () => {
          const latestCasetableReportAttachment = _.head(
            _.sortBy(
              getAttachmentsForCasetafelReport(
                data?.caseTable?.find((ct) => ct.id === approveRejectCasetafelId),
                attachments
              ),
              (att) => att.createdAt
            ).reverse()
          );
          if (latestCasetableReportAttachment) {
            attachmentsBff
              .updateForDossier(params.id, latestCasetableReportAttachment.id, { description: remark })
              .then(() => {
                onSuccess();
                onDossierRefetch();
                onAttachmentsRefetch();
              });
          } else {
            onSuccess();
            onDossierRefetch();
          }
        },
        onError
      }
    );
  };

  const handleUpdated = (value, field, casetafelId) => {
    onUpdated(value, field, 'caseTable', casetafelId);
  };

  const handleStakeholderAddClick = (casetafelId) => {
    setStakeholderCasetafelId(casetafelId);
    stakeholderAddModal.onOpen();
  };

  const handleStakeholderOpenClick = (casetafelId, data, permissions) => {
    if (!permissions?.read || permissions.readReportOnly) {
      return;
    }
    setStakeholderCasetafelId(casetafelId);
    setStakeholderDetailModalData(data);
    setModalPermissions(permissions);
    stakeholderDetailModal.onOpen();
  };

  const handleAnsweredOnQuestionnaire = () => {
    onDossierRefetch();
    toastService.showToast(TOAST_TYPES.SUCCESS, 'Uw antwoorden zijn succesvol verzonden');
  };

  const handleAnswersExport = (casetafelId) => {
    window.open(
      `${BFF_HOST[process.env.NODE_ENV] || window.location.origin}/api/dossiers/${
        params.id
      }/case-tables/${casetafelId}/answers-export?token=${getToken()}`,
      '_self'
    );
  };

  const handleOpenCasetafelAttachment = (att, casetafel, permissions) => {
    if (!permissions.read) {
      return;
    }
    setOpenedCasetafel(casetafel);
    setModalPermissions(permissions);
    attachmentCRUD.onAttachmentOpen(att);
  };

  const handleCloseAttachmentDetail = () => {
    setOpenedCasetafel(null);
    attachmentCRUD.detailsModal.onClose();
  };

  const getCanDelete = () => {
    return permissionsService.canAddCasetafel(user);
  };

  const getCanApprove = (casetafel, casetableAttachments) => {
    return (
      !isLoadingAttachments &&
      getAttachmentsForCasetafelReport(casetafel, attachments)?.length > 0 &&
      ((casetafel.approved !== true && !casetafel.declineDescription) ||
        casetableAttachments?.some((att) => moment(att.createdAt).isAfter(moment(casetafel?.approvedDate))))
    );
  };

  const getCanShare = getCanApprove;

  const getCanDeleteAttachment = () => {
    return (
      attachmentCRUD.attachmentDetailForm?.data?.type !== ATTACHMENT_TYPES.CASETAFEL_REPORT ||
      permissionsService.canDeleteCasetafelReport(user, openedCasetafel?.involved)
    );
  };

  const descriptionLabel =
    attachmentCRUD.openedAttachmentType === ATTACHMENT_TYPES.CASETAFEL
      ? 'Beschrijving van het bestand'
      : 'Zijn er nog specifieke vragen waarop een antwoord van de school gewenst is?';

  return (
    <div>
      <DossierSectionWrapper
        title="Commissies"
        onAddItem={handleAdd}
        addLabel="Commissie Toevoegen"
        noItemsLabel="Er zijn geen commissies toegevoegd."
        isAdding={creatingCasetafel}
        openedItemIds={openedCasetables}
        onItemHeaderClick={(open, id) => {
          const newValue = open ? [...(openedCasetables ?? []), id] : openedCasetables?.filter((oc) => oc !== id) ?? [];
          setOpenedCasetables(newValue);
        }}
        items={
          data?.caseTable?.map((commission) => {
            const commissionPermissions = dossierTabsPermissionsService.toCommissionPermissions(
              commission,
              permissions,
              user
            );
            return {
              id: commission.id,
              title: commissionToTitle(commission),
              Component: (
                <>
                  <DossierCasetafelStakeholders
                    {...commission}
                    {...commissionPermissions}
                    onStakeholderOpen={(stakeholder) =>
                      handleStakeholderOpenClick(commission.id, stakeholder, commissionPermissions)
                    }
                    onStakeholderAdd={() => handleStakeholderAddClick(commission.id)}
                  />
                  <DossierCasetafelDetails
                    {...commission}
                    {...commissionPermissions}
                    isCommission
                    dossierId={params.id}
                    onUpdated={handleUpdated}
                  />
                  <DossierCasetafelQuestionnaire
                    {...commission}
                    {...commissionPermissions}
                    onUpdated={handleUpdated}
                    user={user}
                    dossierId={params.id}
                    formOptions={forms}
                    isLoadingForms={isLoadingForms}
                    onAnswered={handleAnsweredOnQuestionnaire}
                    onAnswersExport={handleAnswersExport}
                    isRepresentative={permissionsService.isCasetafelRepresentative(user, commission.involved)}
                  />
                  <DossierCasetafelAttachments
                    {...commissionPermissions}
                    dossierType={data?.type}
                    loadingAttachments={isLoadingAttachments}
                    attachments={getAttachmentsForCasetafel(commission, attachments)}
                    reports={getAttachmentsForCasetafelReport(commission, attachments)}
                    onAttachmentAdd={() =>
                      attachmentCRUD.onAttachmentAddOpen(commission.id, ATTACHMENT_TYPES.CASETAFEL)
                    }
                    onAttachmentOpen={(att) => handleOpenCasetafelAttachment(att, commission, commissionPermissions)}
                    onReportAdd={
                      permissionsService.canAddCasetafelReport(user, commission?.involved)
                        ? () => attachmentCRUD.onAttachmentAddOpen(commission.id, ATTACHMENT_TYPES.CASETAFEL_REPORT)
                        : undefined
                    }
                    onReportOpen={(att) => handleOpenCasetafelAttachment(att, commission, commissionPermissions)}
                    isCommission
                  />
                  <DossierSubitemActionBar
                    {...commission}
                    openedCasetableId={commission.id}
                    actions={
                      getCanApprove(commission, getAttachmentsForCasetafelReport(commission, attachments)) && (
                        <>
                          <Button
                            className="u-margin-right-sm"
                            type="success"
                            iconRight="ai-check-1"
                            onClick={() => handleRequestApprove(commission.id)}
                          >
                            School bevestigt verslag
                          </Button>
                          {/* <Button
                            className="u-margin-right-sm"
                            type="danger"
                            iconRight="ai-close"
                            onClick={() => handleRequestReject(commission.id)}
                          >
                            Niet akkoord
                          </Button> */}
                        </>
                      )
                    }
                    canDelete={getCanDelete()}
                    canShare={getCanShare(commission)}
                    onDelete={handleRequestDelete}
                    mailToLink={`mailto:?subject=${encodeURIComponent('Advies commissie')}`}
                  />
                </>
              )
            };
          }) ?? []
        }
      />
      <AttachmentUploadModal
        {...attachmentCRUD.uploadModal}
        onConfirm={attachmentCRUD.onAttachmentAdd}
        descriptionLabel={descriptionLabel}
      />
      <AttachmentDetailsModal
        {...attachmentCRUD.detailsModal}
        {...modalPermissions}
        onClose={handleCloseAttachmentDetail}
        form={attachmentCRUD.attachmentDetailForm}
        dossierId={params.id}
        onUpdate={attachmentCRUD.onAttachmentUpdate}
        onUpdateSuccess={attachmentCRUD.onAttachmentsRefetch}
        onDelete={getCanDeleteAttachment() ? attachmentCRUD.deleteModal.onOpen : undefined}
        descriptionLabel={descriptionLabel}
      />

      <DeleteWarningModal
        {...deleteModal}
        warningText={`Ben je zeker dat je deze ${
          data?.type === DOSSIER_TYPES.FINANCED_PROGRAM ? 'commissie' : 'casetafel'
        } wilt verwijderen? Alle informatie die hieraan gekoppeld is zal verloren gaan.`}
        onConfirm={handleDelete}
      />
      <CasetafelApproveModal
        {...approveModal}
        onConfirm={handleApprove}
        isCommission={data?.type === DOSSIER_TYPES.FINANCED_PROGRAM}
      />
      <CasetafelRejectModal
        {...rejectModal}
        onConfirm={handleReject}
        isCommission={data?.type === DOSSIER_TYPES.FINANCED_PROGRAM}
      />
      <StakeholderAddModal
        {...stakeholderAddModal}
        onUpdated={handleUpdated}
        dossierSchools={data?.overview?.school ?? []}
        dossierId={params.id}
        dossierPath={STAKEHOLDER_DOSSIER_PATHS.COMMISSION}
        itemId={stakeholderCasetafelId}
      />
      {stakeholderDetailModal.isOpen && (
        <StakeholderDetailModal
          {...stakeholderDetailModal}
          {...modalPermissions}
          data={stakeholderDetailModalData}
          onUpdated={handleUpdated}
          dossierId={params.id}
          dossierPath={STAKEHOLDER_DOSSIER_PATHS.COMMISSION}
          itemId={stakeholderCasetafelId}
        />
      )}
      {attachmentCRUD.deleteModal.isOpen && (
        <DeleteWarningModal
          {...attachmentCRUD.deleteModal}
          warningText="Weet je zeker dat je deze bijlage wilt verwijderen?"
          onConfirm={attachmentCRUD.onAttachmentDelete}
        />
      )}
    </div>
  );
};

export default DossierDetailCommission;
